/**
 * Calls page — Recent calls + Voicemails for the connected Telzio user.
 * Shows a Connect Telzio CTA if not yet connected.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Voicemail, Loader2,
  Trash2, CheckCircle, Play, Pause, Link2, Bell, RefreshCw, Plug, LogOut,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';
import TelzioConnectModal from './TelzioConnectModal';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDuration = (s) => {
  if (!s || s < 1) return '—';
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

// Telzio "from"/"to" can be a UserCallerType, PhoneNumberCallerType, or
// FeatureCodeCallerType object — or a plain string for legacy webhooks.
const callerLabel = (caller) => {
  if (!caller) return '—';
  if (typeof caller === 'string') return caller;
  if (caller.user) {
    const u = caller.user;
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    return name || u.username || (u.extension ? `ext ${u.extension}` : 'User');
  }
  if (caller.phone_number) {
    const p = caller.phone_number;
    return p.cnam || p.local_format || p.international_format || p.e164 || p.raw || 'Unknown';
  }
  if (caller.feature_code) return caller.feature_code.name || 'Feature code';
  return caller.type || 'Unknown';
};

// Returns an E.164 phone number for a caller object (or null if not callable)
const callerE164 = (caller) => {
  if (!caller) return null;
  if (typeof caller === 'string') {
    const s = caller.replace(/[^\d+]/g, '');
    return s.startsWith('+') ? s : (s.length >= 10 ? `+${s}` : null);
  }
  if (caller.phone_number) {
    return caller.phone_number.e164 || caller.phone_number.raw || null;
  }
  // User-to-user (extension) calls aren't dial-out targets
  return null;
};

// ═══════════════ STATUS BAR ═══════════════
const StatusBar = ({ status, badges, onConnect, onDisconnect, onRefresh }) => {
  if (!status?.connected) {
    return (
      <Card className="bg-brand/5 border-brand/20" data-testid="telzio-disconnected-card">
        <CardContent className="p-4 flex items-center gap-3">
          <Phone size={20} className="text-brand" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-text-primary">Telzio not connected</p>
            <p className="text-xs text-text-tertiary">Sign in with your Telzio account to see calls, voicemails, and place calls from Ventas OS.</p>
          </div>
          <Button size="sm" onClick={onConnect} className="bg-brand hover:bg-brand-hover" data-testid="telzio-connect-btn">
            <Plug size={14} className="mr-1" /> Connect Telzio
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-emerald-50 border-emerald-200" data-testid="telzio-connected-card">
      <CardContent className="p-3 flex items-center gap-3">
        <CheckCircle size={18} className="text-emerald-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-emerald-900">
            {status.first_name} {status.last_name} · ext {status.extension}
          </p>
          <p className="text-xs text-emerald-700">
            {status.company} · {status.available_numbers_count} numbers available
            {badges ? (
              <>
                {' · '}
                <span className="font-semibold">{badges.voicemails || 0}</span> new voicemails
                {' · '}
                <span className="font-semibold">{badges.missed_calls || 0}</span> missed calls
              </>
            ) : null}
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={onRefresh} title="Refresh" data-testid="telzio-refresh">
          <RefreshCw size={14} />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDisconnect} title="Disconnect" data-testid="telzio-disconnect">
          <LogOut size={14} className="text-red-500" />
        </Button>
      </CardContent>
    </Card>
  );
};

// Click-to-call button — dispatches a global event so the integrated
// browser softphone (if connected) handles the call. Falls back to a
// standard `tel:` link if the softphone widget isn't mounted/registered.
const CallButton = ({ e164, label, size = 'sm', className = '' }) => {
  if (!e164) return null;
  const onClick = (ev) => {
    ev.preventDefault();
    window.dispatchEvent(new CustomEvent('ventas:softphone:call', {
      detail: { e164, label },
    }));
  };
  return (
    <a
      href={`tel:${e164}`}
      onClick={onClick}
      data-testid={`call-btn-${e164}`}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-brand/10 text-brand hover:bg-brand/20 transition-colors ${className}`}
      title={`Call ${e164}`}
    >
      <Phone size={11} /> Call
    </a>
  );
};

// ═══════════════ CALLS LIST ═══════════════
const CallRow = ({ call }) => {
  const dir = (call.direction || '').toLowerCase();
  const status = (call.call_status || call.status || '').toLowerCase();
  const isMissed = status === 'no-answer' || status === 'no_answer' || status === 'missed' || status === 'busy' || status === 'failed' || status === 'canceled' || status === 'aborted';
  const Icon = isMissed ? PhoneMissed : (dir === 'inbound' ? PhoneIncoming : PhoneOutgoing);
  const iconColor = isMissed ? 'text-red-500' : (dir === 'inbound' ? 'text-blue-500' : 'text-emerald-500');
  const fromLabel = callerLabel(call.from);
  const toLabel = callerLabel(call.to);
  const date = call.date || call.created || call.start_time || call.created_at;
  // For inbound, we'd call back the external "from"; for outbound, redial the "to".
  const callbackE164 = dir === 'inbound' ? callerE164(call.from) : callerE164(call.to);
  const callbackLabel = dir === 'inbound' ? fromLabel : toLabel;

  return (
    <div className="flex items-center gap-3 py-2 px-3 hover:bg-surface-page rounded-lg" data-testid={`call-${call.call_id}`}>
      <Icon size={16} className={iconColor} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-text-primary truncate">
          {dir === 'inbound' ? <>From <b>{fromLabel}</b> &rarr; {toLabel}</> : <>To <b>{toLabel}</b></>}
        </p>
        <p className="text-xs text-text-tertiary">{formatDate(date)}</p>
      </div>
      <CallButton e164={callbackE164} label={callbackLabel} />
      <span className="text-xs text-text-secondary tabular-nums">{formatDuration(call.duration)}</span>
    </div>
  );
};

const CallsList = ({ calls, loading }) => {
  if (loading) return <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-brand" /></div>;
  if (!calls.length) return <p className="text-sm text-text-tertiary text-center py-8" data-testid="no-calls">No calls in this period.</p>;
  return (
    <div className="space-y-1" data-testid="calls-list">
      {calls.map(c => <CallRow key={c.call_id || c.id} call={c} />)}
    </div>
  );
};

// ═══════════════ VOICEMAIL ROW ═══════════════
const VoicemailRow = ({ vm, onAction }) => {
  const [playing, setPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = React.useRef(null);

  const startPlayback = async () => {
    if (audioUrl) {
      audioRef.current?.play();
      setPlaying(true);
      return;
    }
    try {
      const res = await authFetch(`${API}/api/telzio/voicemails/${vm.voicemail_id || vm.id}/audio`);
      if (!res.ok) throw new Error('Audio stream failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      // Mark listened on first play (status enum per Telzio v3 spec)
      if ((vm.status || '').toLowerCase() !== 'listened' && !vm.listened_date) {
        await authFetch(`${API}/api/telzio/voicemails/${vm.voicemail_id || vm.id}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'listened' }),
        });
        onAction?.();
      }
      setTimeout(() => audioRef.current?.play(), 50);
      setPlaying(true);
    } catch (e) { toast.error(e.message); }
  };

  const stopPlayback = () => {
    audioRef.current?.pause();
    setPlaying(false);
  };

  const removeVm = async () => {
    if (!window.confirm('Delete this voicemail?')) return;
    try {
      const res = await authFetch(`${API}/api/telzio/voicemails/${vm.voicemail_id || vm.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Voicemail deleted');
      onAction?.();
    } catch (e) { toast.error(e.message); }
  };

  const isUnread = (vm.status || '').toLowerCase() === 'unlistened' || (!vm.listened_date && !vm.listened && !vm.status);

  return (
    <Card className={isUnread ? 'border-brand/40' : ''} data-testid={`vm-${vm.voicemail_id || vm.id}`}>
      <CardContent className="p-3 flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isUnread ? 'bg-brand text-white' : 'bg-surface-page text-text-muted'}`}>
          <Voicemail size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <p className="text-sm font-medium text-text-primary truncate">
              {callerLabel(vm.from)}
              {isUnread && <span className="ml-2 text-[10px] font-semibold text-brand uppercase">New</span>}
            </p>
            <span className="text-xs text-text-tertiary flex-shrink-0">{formatDate(vm.created || vm.date || vm.created_at)}</span>
          </div>
          {vm.transcription && (
            <p className="text-xs text-text-secondary line-clamp-2 mb-2">{vm.transcription}</p>
          )}
          <div className="flex items-center gap-2 text-xs">
            <Button size="sm" variant="ghost" onClick={playing ? stopPlayback : startPlayback} data-testid={`vm-play-${vm.voicemail_id || vm.id}`}>
              {playing ? <Pause size={12} className="mr-1" /> : <Play size={12} className="mr-1" />}
              {playing ? 'Pause' : `Play (${formatDuration(vm.duration)})`}
            </Button>
            <CallButton e164={callerE164(vm.from)} label={callerLabel(vm.from)} />
            <Button size="sm" variant="ghost" onClick={removeVm} data-testid={`vm-delete-${vm.voicemail_id || vm.id}`}>
              <Trash2 size={12} className="text-red-500" />
            </Button>
            {audioUrl && (
              <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={() => setPlaying(false)}
                onPause={() => setPlaying(false)}
                className="hidden"
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════ MAIN PAGE ═══════════════
const Calls = () => {
  const [status, setStatus] = useState(null);
  const [badges, setBadges] = useState(null);
  const [calls, setCalls] = useState([]);
  const [voicemails, setVoicemails] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callsLoading, setCallsLoading] = useState(false);
  const [vmLoading, setVmLoading] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [tab, setTab] = useState('voicemails');

  const loadStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/telzio/status`);
      const data = await res.json();
      setStatus(data);
      return data.connected;
    } catch { return false; }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const connected = await loadStatus();
    if (!connected) { setLoading(false); return; }
    try {
      const [bRes, eRes] = await Promise.all([
        authFetch(`${API}/api/telzio/badges`),
        authFetch(`${API}/api/telzio/events?limit=20`),
      ]);
      if (bRes.ok) setBadges(await bRes.json());
      if (eRes.ok) setEvents((await eRes.json()).events || []);
    } catch { /* */ }
    finally { setLoading(false); }
  }, [loadStatus]);

  const loadCalls = useCallback(async () => {
    if (!status?.connected) return;
    setCallsLoading(true);
    try {
      const res = await authFetch(`${API}/api/telzio/calls/recent?limit=50`);
      if (res.ok) {
        const d = await res.json();
        setCalls(d.data || d.items || []);
      }
    } catch { /* */ }
    finally { setCallsLoading(false); }
  }, [status]);

  const loadVoicemails = useCallback(async () => {
    if (!status?.connected) return;
    setVmLoading(true);
    try {
      const res = await authFetch(`${API}/api/telzio/voicemails?limit=50`);
      if (res.ok) {
        const d = await res.json();
        setVoicemails(d.data || d.items || []);
      }
    } catch { /* */ }
    finally { setVmLoading(false); }
  }, [status]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (status?.connected) { loadCalls(); loadVoicemails(); } }, [status, loadCalls, loadVoicemails]);

  // Poll for new events / badges every 30s while connected
  useEffect(() => {
    if (!status?.connected) return;
    const tick = setInterval(async () => {
      try {
        const [bRes, eRes] = await Promise.all([
          authFetch(`${API}/api/telzio/badges`),
          authFetch(`${API}/api/telzio/events?limit=20`),
        ]);
        if (bRes.ok) {
          const newBadges = await bRes.json();
          // Toast for new voicemail since last poll
          if (badges && newBadges.voicemails > badges.voicemails) {
            toast.info(`New voicemail (${newBadges.voicemails} unread)`);
            loadVoicemails();
          }
          setBadges(newBadges);
        }
        if (eRes.ok) {
          const data = await eRes.json();
          // Toast for fresh inbound calls
          const fresh = (data.events || []).filter(e =>
            e.event_type === 'call_created' &&
            new Date(e.received_at) > new Date(Date.now() - 60_000)
          );
          if (fresh.length > events.filter(e => e.event_type === 'call_created' && new Date(e.received_at) > new Date(Date.now() - 60_000)).length) {
            const e = fresh[0];
            toast.info(`Incoming call from ${e.payload?.from_cnam || e.payload?.from || 'Unknown'}`);
          }
          setEvents(data.events || []);
        }
      } catch { /* */ }
    }, 30_000);
    return () => clearInterval(tick);
  }, [status, badges, events, loadVoicemails]);

  const disconnect = async () => {
    if (!window.confirm('Disconnect your Telzio account?')) return;
    try {
      await authFetch(`${API}/api/telzio/disconnect`, { method: 'DELETE' });
      toast.success('Telzio disconnected');
      setStatus({ connected: false });
      setCalls([]); setVoicemails([]); setBadges(null);
    } catch { toast.error('Disconnect failed'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand" size={28} /></div>;

  return (
    <div className="space-y-5 max-w-5xl mx-auto p-6" data-testid="calls-page">
      <div className="flex items-center gap-2">
        <Phone size={22} className="text-brand" />
        <h1 className="text-2xl font-bold text-text-primary">Calls & Voicemail</h1>
      </div>

      <StatusBar
        status={status}
        badges={badges}
        onConnect={() => setShowConnect(true)}
        onDisconnect={disconnect}
        onRefresh={loadAll}
      />

      {status?.connected && (
        <>
          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-border-subtle">
            <button
              onClick={() => setTab('voicemails')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'voicemails' ? 'border-brand text-brand' : 'border-transparent text-text-tertiary hover:text-text-primary'}`}
              data-testid="tab-voicemails"
            >
              <Voicemail size={14} className="inline mr-1.5" /> Voicemails ({voicemails.length})
            </button>
            <button
              onClick={() => setTab('calls')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'calls' ? 'border-brand text-brand' : 'border-transparent text-text-tertiary hover:text-text-primary'}`}
              data-testid="tab-calls"
            >
              <Phone size={14} className="inline mr-1.5" /> Recent Calls ({calls.length})
            </button>
            <button
              onClick={() => setTab('events')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'events' ? 'border-brand text-brand' : 'border-transparent text-text-tertiary hover:text-text-primary'}`}
              data-testid="tab-events"
            >
              <Bell size={14} className="inline mr-1.5" /> Activity ({events.length})
            </button>
          </div>

          {tab === 'voicemails' && (
            <div className="space-y-2" data-testid="voicemails-list">
              {vmLoading ? <div className="flex justify-center py-6"><Loader2 className="animate-spin text-brand" /></div>
                : voicemails.length === 0 ? <p className="text-sm text-text-tertiary text-center py-8">No voicemails.</p>
                : voicemails.map(vm => <VoicemailRow key={vm.voicemail_id || vm.id} vm={vm} onAction={loadVoicemails} />)}
            </div>
          )}

          {tab === 'calls' && (
            <Card>
              <CardContent className="p-2">
                <CallsList calls={calls} loading={callsLoading} />
              </CardContent>
            </Card>
          )}

          {tab === 'events' && (
            <Card>
              <CardContent className="p-3 space-y-1">
                {events.length === 0 ? (
                  <p className="text-sm text-text-tertiary text-center py-8">
                    No webhook events yet. Configure your Telzio webhook URL to start receiving real-time call events.
                  </p>
                ) : events.map((e, i) => (
                  <div key={e._id || e.id || `${e.event_type}-${e.received_at || i}`} className="flex items-center gap-2 text-xs py-1.5 border-b border-border-subtle last:border-0" data-testid={`event-${i}`}>
                    <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded bg-surface-page">{e.event_type}</span>
                    <span className="flex-1 truncate text-text-secondary">
                      {e.payload?.from_cnam || e.payload?.from} {e.payload?.to ? `→ ${e.payload.to}` : ''}
                    </span>
                    <span className="text-text-tertiary">{formatDate(e.received_at)}</span>
                  </div>
                ))}
                <div className="mt-3 text-[11px] text-text-tertiary">
                  <Link2 size={10} className="inline mr-1" />
                  Webhook URL for Telzio: <code className="bg-surface-page px-1 rounded">{API}/api/telzio/webhook</code>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <TelzioConnectModal
        open={showConnect}
        onClose={() => setShowConnect(false)}
        onConnected={() => loadAll()}
      />
    </div>
  );
};

export default Calls;
